import type { ApiObject } from '@rudderstack/analytics-js';
import { RudderAnalytics } from '@rudderstack/analytics-js';
import type { AiResponseReceived, AiUserPromptCreated } from '@server/analytics-event-types';
import {
  ANALYTICS_EVENTS,
  type AnalyticsEventName,
  type AnalyticsEventProperties,
  type SingleShapeEvent,
} from '@server/analytics-events';

import { type AppEnvironment, isFrontendAnalyticsEnabled, resolveAppEnvironment } from './resolve-app-environment';
import { routePatternOf } from './route-pattern';

const writeKey = __RUDDERSTACK_ANALYTICS_FRONTEND_WRITE_KEY__;
const dataPlaneUrl = __RUDDERSTACK_ANALYTICS_DATA_PLANE_URL__;

let client: RudderAnalytics | null = null;
let enabled = false;
let appEnvironment: AppEnvironment | null = null;

/**
 * The SDK's own `context.page`, replaced with a sanitised one.
 *
 * `getContextPageProperties` re-reads `location` and `document` on EVERY event — not only `page()`
 * calls — and returns `{path, referrer, referring_domain, search, title, url, tab_url}`. Left alone
 * that ships the resolved pathname (`/w/acme/audiences/aud_123`), the query string, a `/pub/:token`
 * share token, and `document.title`, which `usePageTitle` sets from the audience or chat title the
 * customer wrote.
 *
 * The SDK merges `options.context` over its own after building it, and `page` is not one of its
 * reserved keys, so supplying every key here replaces the lot. Each key must be present: a key left
 * out keeps the live value. There are nine — the seven `getDefaultPageProperties` reads, plus the two
 * `getContextPageProperties` appends.
 */
export function sanitisedPage(): Record<string, string> {
  const pattern = routePatternOf(window.location.pathname);
  const url = `${window.location.origin}${pattern}`;
  // The current page is reduced to its route pattern, with search/title blanked. In-app and first-touch
  // referrers are blanked; a current external referrer keeps only its origin and host for attribution.
  let externalReferrer = { origin: '', host: '' };
  if (document.referrer !== '') {
    try {
      const parsedReferrer = new URL(document.referrer);
      if (parsedReferrer.origin !== window.location.origin) {
        externalReferrer = { origin: parsedReferrer.origin, host: parsedReferrer.host };
      }
    } catch {
      // Malformed referrers are not useful attribution, and must not break analytics callers.
    }
  }
  return {
    path: pattern,
    url,
    tab_url: url,
    search: '',
    title: '',
    referrer: externalReferrer.origin,
    referring_domain: externalReferrer.host,
    // `getContextPageProperties` appends these two after the seven above, from a referrer the SDK
    // PERSISTS across visits. A tab opened from `/w/acme/audiences/aud_123` stamps that URL on every
    // later event until site data is cleared. The per-page external check above cannot vet a value
    // captured on an earlier page, so first-touch attribution is dropped rather than risked.
    initial_referrer: '',
    initial_referring_domain: '',
  };
}

function analyticsContext(): { appEnvironment: AppEnvironment; page: Record<string, string> } {
  return { appEnvironment: appEnvironment!, page: sanitisedPage() };
}

export function initAnalytics(): void {
  if (!writeKey || !dataPlaneUrl) return;

  if (!isFrontendAnalyticsEnabled(window.location.hostname)) return;

  appEnvironment = resolveAppEnvironment(window.location.hostname);

  try {
    client = new RudderAnalytics();
    client.load(writeKey, dataPlaneUrl, {});
    enabled = true;
  } catch (error) {
    console.warn('Error initializing analytics', error);
    client = null;
    enabled = false;
    appEnvironment = null;
  }
}

function getClient(): RudderAnalytics | null {
  return enabled ? client : null;
}

export function identifyUser(userId: string, traits: { email?: string | null; displayName?: string | null }): void {
  const analytics = getClient();
  if (!analytics) return;
  analytics.identify(
    userId,
    {
      email: traits.email ?? undefined,
      name: traits.displayName ?? undefined,
    },
    { context: analyticsContext() },
  );
}

export function groupWorkspace(groupId: string, traits: { slug: string; role: string }): void {
  const analytics = getClient();
  if (!analytics) return;
  analytics.group(groupId, traits, { context: analyticsContext() });
}

export function resetAnalytics(): void {
  const analytics = getClient();
  if (!analytics) return;
  analytics.reset();
}

export function trackClientEvent<T extends AnalyticsEventName>(
  event: T,
  // Same union guard as the backend `TrackInput`.
  properties: AnalyticsEventProperties[T] & SingleShapeEvent<T>,
): void {
  const analytics = getClient();
  if (!analytics) return;
  // Telemetry never decides what the person's click does: callers run this before the action itself.
  try {
    analytics.track(event, properties as ApiObject, { context: analyticsContext() });
  } catch {
    // The SDK catches its own failures, so this is only what runs around it. Nothing useful to do with it here.
  }
}

type SnakeCase<S extends string> = S extends `${infer Head}${infer Tail}`
  ? `${Head extends Lowercase<Head> ? Head : `_${Lowercase<Head>}`}${SnakeCase<Tail>}`
  : S;
// rudder-cli types these props in camelCase and snake_cases them on the wire; trackAi sends wire names.
type AiWireProperties<T> = {
  [Key in keyof Omit<T, 'source' | 'feature' | 'surface'> as SnakeCase<Key & string>]: undefined extends T[Key]
    ? T[Key] | null
    : T[Key];
};
export interface AiClientContext {
  accountId: string;
  feature: string;
  surface: NonNullable<AiResponseReceived['surface']>;
}

interface AiClientProperties {
  ai_user_prompt_created: AiWireProperties<AiUserPromptCreated>;
  ai_response_received: AiWireProperties<AiResponseReceived>;
}

/** AI events client copies (`source: 'client'`). Never sampled: they are matched per `interaction_id`. */
export function trackAi<T extends keyof AiClientProperties>(
  event: T,
  properties: AiClientProperties[T],
  chatContext: AiClientContext,
): void {
  const analytics = getClient();
  if (!analytics) return;
  const defined = Object.fromEntries(Object.entries(properties).filter(([, value]) => value != null));
  analytics.track(
    event,
    {
      ...defined,
      source: 'client',
      account_id: chatContext.accountId,
      feature: chatContext.feature,
      surface: chatContext.surface,
    },
    { context: { ...analyticsContext(), groupId: chatContext.accountId } },
  );
}

export { ANALYTICS_EVENTS };
